import { useEffect } from "react"
import { motion, AnimatePresence } from "motion/react"
import { X } from "@untitledui/icons"

// "Are you sure you want to leave?" overlay used by every estimate
// wizard. Bodyscroll is locked while open so the underlying card
// can't scroll out from under the dialog. Each flow passes its own
// copy.

interface ConfirmLeaveProps {
  open: boolean
  title: string
  body: string
  continueLabel: string
  exitLabel: string
  onCancel: () => void
  onConfirm: () => void
}

export function ConfirmLeave({
  open,
  title,
  body,
  continueLabel,
  exitLabel,
  onCancel,
  onConfirm,
}: ConfirmLeaveProps) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4 pb-4 sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <button
            type="button"
            aria-label={continueLabel}
            onClick={onCancel}
            className="absolute inset-0 bg-zinc-900/55 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="relative z-10 w-full sm:max-w-md bg-white rounded-3xl shadow-2xl shadow-zinc-900/30"
          >
            <div
              className="px-5 py-5 sm:px-6 sm:py-6"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 20px)" }}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-[18px] font-bold text-zinc-900 tracking-tight leading-tight">
                  {title}
                </h3>
                <button
                  type="button"
                  aria-label={continueLabel}
                  onClick={onCancel}
                  className="size-8 rounded-lg hover:bg-zinc-100 grid place-items-center text-zinc-500"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </div>
              <p className="text-[13.5px] text-zinc-500 leading-relaxed mb-5">
                {body}
              </p>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[13.5px] font-semibold text-zinc-800 transition-colors duration-150"
                >
                  {continueLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  className="h-11 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold transition-colors duration-150"
                >
                  {exitLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
